// Instagram Channel Service for frontend-react
// Uses Conversations API for Instagram channel connection

import { CONVERSATIONS_API_URL, META_INTEGRATION_ENABLED, getConversationsAuthToken } from '@/config/env'

export interface InstagramConnectionStatus {
  connected: boolean
  /** Instagram Business Account ID — the API returns it as `account_id`. */
  account_id?: string
  page_id?: string
  metadata?: {
    instagram_account_id?: string
    page_id?: string
    page_name?: string
    instagram_username?: string
    instagram_name?: string
    [key: string]: unknown
  }
}

export interface InstagramConnectData {
  instagram_account_id: string
  page_access_token: string
  page_id: string
  /** Lets the backend derive a long-lived Page token instead of storing the short-lived one. */
  user_access_token?: string
  redirect_uri?: string
}

export class InstagramServiceError extends Error {
  public status?: number
  
  constructor(message: string, status?: number) {
    super(message)
    this.status = status
    this.name = 'InstagramServiceError'
  }
}

class InstagramServiceClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = CONVERSATIONS_API_URL || ''
  }

  private getAuthToken(): string {
    return getConversationsAuthToken()
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (!META_INTEGRATION_ENABLED) {
      throw new InstagramServiceError('Meta integration is disabled in this environment', 503)
    }

    const url = `${this.baseUrl}${endpoint}`
    
    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options
    }

    // Add authentication token
    const token = this.getAuthToken()
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Token ${token}`,  // Use Token auth for user-specific endpoints
      }
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new InstagramServiceError(
          `HTTP ${response.status}: ${errorText || response.statusText}`,
          response.status
        )
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json()
        return data
      } else {
        const text = await response.text()
        return text as T
      }
    } catch (error) {
      if (error instanceof InstagramServiceError) {
        throw error
      }
      throw new InstagramServiceError(`Network error: ${error}`, 0)
    }
  }

  /**
   * Connect Instagram channel
   */
  async connect(data: InstagramConnectData): Promise<InstagramConnectionStatus> {
    return this.request<InstagramConnectionStatus>('/api/v1/auth/instagram/connect/', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Connect through the Instagram API with Instagram Login.
   *
   * Uses the `instagram_business_*` permissions, which are approved separately
   * from the Page-based flow's `instagram_manage_messages`.
   */
  async connectWithInstagramLogin(code: string, redirectUri: string): Promise<InstagramConnectionStatus> {
    return this.request<InstagramConnectionStatus>('/api/v1/auth/instagram/connect/', {
      method: 'POST',
      body: JSON.stringify({
        auth_flow: 'instagram_login',
        code,
        redirect_uri: redirectUri,
      }),
    })
  }

  /**
   * Disconnect Instagram channel
   */
  async disconnect(): Promise<void> {
    return this.request<void>('/api/v1/auth/instagram/disconnect/', {
      method: 'POST'
    })
  }

  /**
   * Get Instagram connection status
   */
  async getStatus(): Promise<InstagramConnectionStatus> {
    const response = await this.request<InstagramConnectionStatus>('/api/v1/auth/instagram/status/')
    return response
  }
}

// Export singleton instance
export const instagramService = new InstagramServiceClient()

